import React, { useEffect, useState, useRef, useMemo } from 'react'
import classNames from 'classnames'
import { CheckBox, Input } from '@st-operations/uikit/src/v2/index.js'

import {
  getUnifiedUserProfileSchema,
  getUnifiedUserProfile,
  updateUnifiedUserProfile,
  deleteUnifiedUserProfile,
} from '_services/data-explorer'

import { useNavigationContext } from '../../contexts/NavigationContext.jsx'
import { useRetractSidebar } from '_hooks'
import Header from './header/index.jsx'
import { logErrorAsync } from '../../lib/utility.js'
import ResizableColumn from '_components/resizable-column/index.jsx'
import MultiSelectPanel from './query-panel/multi-select-panel/index.jsx'
import Collapsible from '../event-feeds/query-panel/collapsible/index.jsx'
import FirstPartyForm from './first-party-form/index.jsx'
import DetailsForm from './details-form/index.jsx'
import CustomCheckbox from '_components/custom-checkbox/index.jsx'
import InfoTooltip from '_components/info-tooltip/index.jsx'
import CustomSelect from '_components/custom-select/index.jsx'

const UnifiedUserProfilePage = ({
  match: {
    params: { id },
  },
}) => {
  const containerRef = useRef(null)
  const { setRemovePadding, setRemoveOverflow } = useNavigationContext()
  useRetractSidebar()

  const [schema, setSchema] = useState(null)
  const [data, setData] = useState({})
  const [firstPartyDataEnabled, setFirstPartyDataEnabled] = useState(false)
  const [loadingSchema, setLoadingSchema] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const loading = useMemo(() => loadingSchema || loadingData, [loadingSchema, loadingData])

  const primaryKeyOptions = useMemo(() => {
    return schema?.primary_key?.enum || []
  }, [schema])

  const geosOptions = useMemo(() => {
    return (
      schema?.geos?.enum?.map((geo) => ({
        value: geo.code,
        label: geo.code,
      })) || []
    )
  }, [schema])

  const dmaCodesOptions = useMemo(() => {
    return (
      schema?.dma_codes?.enum?.map((dmaCode) => ({
        value: `${dmaCode}`,
        label: `${dmaCode}`,
      })) || []
    )
  }, [schema])

  const categoryPredictorsOptionsGrouped = useMemo(
    () =>
      transformGroupedEnum(schema?.uup?.category_predictors?.enum, {
        generateAll: true,
        valueKey: 'value',
      }),
    [schema]
  )

  const b2bPredictorsOptionsGrouped = useMemo(
    () => transformGroupedEnum(schema?.uup?.b2b_predictors?.enum),
    [schema, transformGroupedEnum]
  )

  const psychographicsOptionsGrouped = useMemo(
    () => transformGroupedEnum(schema?.uup?.psychographics?.enum),
    [schema, transformGroupedEnum]
  )

  const demographicsOptionsGrouped = useMemo(
    () =>
      transformGroupedEnum(schema?.uup?.demographics?.enum, {
        valueKey: 'value',
      }),
    [schema, transformGroupedEnum]
  )

  const isHem = useMemo(
    () =>
      data?.primaryKey?.startsWith('hem_') ||
      data?.idMappings?.ids?.some((id) => id.startsWith('hem_')),
    [data?.primaryKey, data?.idMappings?.ids]
  )
  const MAIDS_IDS = ['android_id', 'ios_id']
  const isMaid = useMemo(
    () =>
      MAIDS_IDS.includes(data?.primaryKey) ||
      data?.idMappings?.ids?.some((id) => MAIDS_IDS.includes(id)),
    [data?.primaryKey, data?.idMappings?.ids]
  )

  const configurationSchema = useMemo(() => {
    return [
      {
        key: 'categoryPredictors',
        name: 'Category Predictors',
        optionsGrouped: categoryPredictorsOptionsGrouped,
        options: categoryPredictorsOptionsGrouped?.all
          ? [
              {
                value: 'all',
                label: 'all',
              },
              ...categoryPredictorsOptionsGrouped.all,
            ]
          : [],
      },
      {
        key: 'b2bPredictors',
        name: 'B2B Predictors',
        optionsGrouped: b2bPredictorsOptionsGrouped,
        options: b2bPredictorsOptionsGrouped?.all
          ? [
              {
                value: 'all',
                label: 'all',
              },
              ...b2bPredictorsOptionsGrouped.all,
            ]
          : [],
        hideShowValue: true,
      },
      {
        key: 'psychographics',
        name: 'Psychographics',
        optionsGrouped: psychographicsOptionsGrouped,
        options: psychographicsOptionsGrouped?.all
          ? [
              {
                value: 'all',
                label: 'all',
              },
              ...psychographicsOptionsGrouped.all,
            ]
          : [],
        hideShowValue: true,
      },
      {
        key: 'demographics',
        name: 'Demographics',
        // eslint-disable-next-line react/display-name
        headerAddon: (
          <If condition={data?.uup?.demographics?.length}>
            <div className="flex flex-row items-center gap-1">
              <InfoTooltip text="Adds a calculated weight column next to demographic attributes for population adjustment" />
              <CustomCheckbox
                className="text-sm"
                label="Include Weight"
                checked={data?.uup?.includeWeight}
                onChange={(e) => {
                  setData((prevState) => ({
                    ...prevState,
                    uup: {
                      ...prevState.uup,
                      includeWeight: e.target.checked,
                    },
                  }))
                }}
              />
            </div>
          </If>
        ),
        optionsGrouped: demographicsOptionsGrouped,
        options: demographicsOptionsGrouped?.all
          ? [
              {
                value: 'all',
                label: 'all',
              },
              ...demographicsOptionsGrouped.all,
            ]
          : [],
      },
    ]
  }, [
    categoryPredictorsOptionsGrouped,
    b2bPredictorsOptionsGrouped,
    psychographicsOptionsGrouped,
    demographicsOptionsGrouped,
    data?.uup?.demographics?.length > 0,
    data?.uup?.includeWeight,
  ])

  const handleSave = async (newData) => {
    try {
      setLoadingData(true)

      const response = await updateUnifiedUserProfile(id, newData)
      setData(response)
      toast('Successfully saved')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setLoadingData(false)
    }
  }

  const handleDelete = async () => {
    try {
      setLoadingData(true)
      await deleteUnifiedUserProfile(id)
      history.back()
      toast('Successfully deleted')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setLoadingData((prevState) => ({ ...prevState, isDeleting: false }))
    }
  }

  function handleFirstPartyDataToggle(enabled) {
    setData((prevState) => ({
      ...prevState,
      firstPartyData: enabled ? { format: 'csv', hasHeader: true, delimiter: ',' } : {},
    }))
    setFirstPartyDataEnabled(enabled)
  }

  function handleFistPartyDataChange(firstPartyData) {
    setData((prevState) => ({
      ...prevState,
      firstPartyData,
    }))
  }

  function handleDetailsDataChange(detailsData) {
    setData((prevState) => ({
      ...prevState,
      ...detailsData,
    }))
  }

  useEffect(function configTemplate() {
    setRemovePadding(true)
    setRemoveOverflow(true)

    return () => {
      setRemovePadding(false)
      setRemoveOverflow(false)
    }
  })

  useEffect(function loadSchema() {
    const fetchSchema = async () => {
      try {
        setLoadingSchema(true)
        const response = await getUnifiedUserProfileSchema()
        setSchema(response)
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      } finally {
        setLoadingSchema(false)
      }
    }

    fetchSchema()
  }, [])

  useEffect(
    function loadData() {
      const fetchData = async () => {
        try {
          setLoadingData(true)
          const response = await getUnifiedUserProfile(id)
          setData(response)
          setFirstPartyDataEnabled(Object.keys(response?.firstPartyData || {}).length)
        } catch (error) {
          logErrorAsync(error)
          toast.error(error.message)
        } finally {
          setLoadingData(false)
        }
      }

      fetchData()
    },
    [id]
  )

  useEffect(() => {
    if (data?.uup?.demographics?.length > 0) {
      return
    }

    const newData = {
      ...data,
      uup: {
        ...data.uup,
      },
    }
    delete newData.uup?.includeWeight
    setData(newData)
  }, [data?.uup?.demographics?.length > 0])

  useEffect(
    function refreshHemProviders() {
      if (isHem) return
      setData((prevState) => ({
        ...prevState,
        hems: {
          includeLiveintentHem: false,
          includeSovrnHem: false,
          includeYobiHem: false,
        },
      }))
    },
    [isHem]
  )

  useEffect(
    function refreshMaidProviders() {
      if (isMaid) return
      setData((prevState) => ({
        ...prevState,
        maids: {
          includeYobiMaids: false,
          includeTapadMaids: false,
          includeId5CdgMaids: false,
        },
      }))
    },
    [isMaid]
  )

  function handleHemToggle(checked, key) {
    setData((prevState) => ({
      ...prevState,
      hems: {
        ...prevState.hems,
        [key]: checked,
      },
    }))
  }

  function handleMaidsToggle(checked, key) {
    setData((prevState) => ({
      ...prevState,
      maids: {
        ...prevState.maids,
        [key]: checked,
      },
    }))
  }

  function handleGeosChange(value) {
    setData((prevState) => ({
      ...prevState,
      geos: value,
    }))
  }

  function handleDmaCodesChange(value) {
    setData((prevState) => ({
      ...prevState,
      dmaCodes: value,
    }))
  }

  function handleDemographicsScoreChange(value) {
    const isEmpty = value === '' || value === null || typeof value === 'undefined'
    if (isEmpty) {
      setData((prevState) => ({
        ...prevState,
        demographicsScore: undefined,
      }))
      return
    }
    const parsedValue = typeof value === 'number' ? value : parseFloat(value)
    if (Number.isNaN(parsedValue)) {
      return
    }
    const clampedValue = Math.max(0, Math.min(1, parsedValue))
    setData((prevState) => ({
      ...prevState,
      demographicsScore: clampedValue,
    }))
  }

  return (
    <div ref={containerRef} className="h-[calc(100vh-70px)]">
      <Header
        loading={loading}
        schema={schema}
        data={data}
        onSave={handleSave}
        onDelete={handleDelete}
      />
      <ResizableColumn
        minWidth={380}
        content={
          <div className="ml-4 w-[calc(100%-2.5rem)] min-h-full flex flex-col justify-between pb-4">
            <section
              className={classNames('uup-query-panel', { 'uup-query-panel--collapsed': false })}
            >
              <div className="pl-2 py-2 flex flex-col gap-4">
                <Collapsible title="Details" showOpenButton={false}>
                  <DetailsForm data={data} onDataChange={handleDetailsDataChange} />
                </Collapsible>

                <Collapsible title="Filters" showOpenButton={false}>
                  <CustomSelect
                    name="geos"
                    label="Geos"
                    isSearchable
                    isClearable
                    value={data?.geos?.map((geo) => ({
                      value: geo,
                      label: geosOptions.find((option) => option.value === geo)?.label || geo,
                    }))}
                    options={geosOptions}
                    onChange={(_, _2, value) => handleGeosChange(value)}
                    isMulti
                  />
                  <CustomSelect
                    name="dmaCodes"
                    label="DMA Codes"
                    isSearchable
                    isClearable
                    value={data?.dmaCodes}
                    options={dmaCodesOptions}
                    onChange={(_, _2, value) => handleDmaCodesChange(value)}
                    isMulti
                  />
                  <Input
                    name="demographicsScore"
                    label="Demographics Score"
                    type="number"
                    value={data?.demographicsScore}
                    onChange={handleDemographicsScoreChange}
                    min={0}
                    max={1}
                    step={0.1}
                  />
                </Collapsible>

                <If condition={isHem}>
                  <Collapsible title="HEM Providers" showOpenButton={false} hideToggle>
                    <div className="flex flex-row items-center gap-6 px-1 pb-2">
                      <CustomCheckbox
                        label="LiveIntent"
                        checked={data?.hems?.includeLiveintentHem}
                        onChange={(e) => handleHemToggle(e.target.checked, 'includeLiveintentHem')}
                      />
                      <CustomCheckbox
                        label="Sovrn"
                        checked={data?.hems?.includeSovrnHem}
                        onChange={(e) => handleHemToggle(e.target.checked, 'includeSovrnHem')}
                      />
                      <CustomCheckbox
                        label="Yobi"
                        checked={data?.hems?.includeYobiHem}
                        onChange={(e) => handleHemToggle(e.target.checked, 'includeYobiHem')}
                      />
                    </div>
                  </Collapsible>
                </If>

                <If condition={isMaid}>
                  <Collapsible title="MAID Providers" showOpenButton={false} hideToggle>
                    <div className="flex flex-row items-center gap-6 px-1 pb-2">
                      <CustomCheckbox
                        label="Yobi"
                        checked={data?.maids?.includeYobiMaids}
                        onChange={(e) => handleMaidsToggle(e.target.checked, 'includeYobiMaids')}
                      />
                      <CustomCheckbox
                        label="Tapad"
                        checked={data?.maids?.includeTapadMaids}
                        onChange={(e) => handleMaidsToggle(e.target.checked, 'includeTapadMaids')}
                      />
                      <CustomCheckbox
                        label="ID5"
                        checked={data?.maids?.includeId5CdgMaids}
                        onChange={(e) => handleMaidsToggle(e.target.checked, 'includeId5CdgMaids')}
                      />
                    </div>
                  </Collapsible>
                </If>

                <Collapsible
                  title={
                    <div className="flex flex-row gap-4 items-center">
                      <CheckBox
                        checked={firstPartyDataEnabled}
                        onChange={(e) => handleFirstPartyDataToggle(e.target.checked)}
                      />
                      <span>First Party Data Enrichment</span>
                    </div>
                  }
                  managedState={[firstPartyDataEnabled]}
                  showOpenButton={false}
                  hideToggle
                >
                  <If condition={firstPartyDataEnabled}>
                    <FirstPartyForm
                      disabled={!firstPartyDataEnabled}
                      idTypeOptions={primaryKeyOptions}
                      data={
                        data.firstPartyData && {
                          ...data.firstPartyData,
                          idType:
                            data.firstPartyData?.idType ||
                            primaryKeyOptions.filter((option) =>
                              data.primaryKey?.includes(option.value)
                            )?.[0]?.value,
                        }
                      }
                      onDataChange={handleFistPartyDataChange}
                    />
                  </If>
                </Collapsible>
              </div>
            </section>
          </div>
        }
      >
        <div className="p-4 bg-gray-100 border-box">
          <div className="text-gray-500 text-xl flex flex-col">
            <For each="schemaItem" index="index" of={configurationSchema}>
              <MultiSelectPanel
                className="bg-white"
                headerAddon={schemaItem.headerAddon}
                title={schemaItem.name}
                availableOptions={schemaItem.options}
                availableOptionsGrouped={schemaItem.optionsGrouped}
                selectedOptions={data?.uup?.[schemaItem.key]?.map(
                  (value) => schemaItem.options.find((option) => option.value === value) || {}
                )}
                extractLabel={(option) => option.label}
                onSelectedOptionsChange={(newSelectedOptions) => {
                  setData((prevState) => ({
                    ...prevState,
                    uup: {
                      ...prevState.uup,
                      [schemaItem.key]: newSelectedOptions?.map((option) => option.value) || [],
                    },
                  }))
                }}
                hideShowValue={schemaItem.hideShowValue}
              />
            </For>
          </div>
        </div>
      </ResizableColumn>
    </div>
  )
}

function transformGroupedEnum(groupedEnum, { generateAll, valueKey } = {}) {
  if (!groupedEnum) {
    return groupedEnum
  }

  const all = []
  const newFormat = Object.keys(groupedEnum).reduce((acc, groupKey) => {
    const optionsMap = groupedEnum[groupKey]
    const options = Object.entries(optionsMap).map(([optionKey, optionValue]) => ({
      value: valueKey ? optionValue?.[valueKey] : optionKey,
      label: optionKey,
      description: optionValue.description,
    }))
    all.push(...options)
    return {
      ...acc,
      [groupKey]: options,
    }
  }, {})

  if (generateAll) {
    newFormat.all = all
  }

  return newFormat
}

export default UnifiedUserProfilePage
